// src/pages/OrgUsers.tsx
import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import AppLayout from "@/components/layout/AppLayout";
import { supabase } from "@/integrations/supabase/client";

import { useEffectiveRole } from "@/hooks/useEffectiveRole";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

type Row = {
  user_id: string | null;
  email: string | null;
  member_role: string | null;
  created_at: string;
};

const roleLabels: Record<string, string> = {
  respondent: "Respondent",
  reviewer: "Reviewer",
  org_admin: "Portfolio Manager",
};

export default function OrgUsers() {
  const { orgId } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { role: effectiveRole } = useEffectiveRole();
  const [orgName, setOrgName] = useState<string>("");
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  // Add/invite user state (email-only membership)
  const [newUserEmail, setNewUserEmail] = useState("");
  const [newUserRole, setNewUserRole] = useState<"respondent" | "reviewer" | "org_admin">("respondent");
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteError, setInviteError] = useState<string | null>(null);

  // Create login account state
  const [newAccountEmail, setNewAccountEmail] = useState("");
  const [newAccountPassword, setNewAccountPassword] = useState("");
  const [newAccountDisplayName, setNewAccountDisplayName] = useState("");
  const [newAccountRole, setNewAccountRole] = useState<"respondent" | "reviewer" | "org_admin">("respondent");
  const [createAccountLoading, setCreateAccountLoading] = useState(false);
  const [createAccountError, setCreateAccountError] = useState<string | null>(null);

  // Check if user can create accounts (admin, client_admin, org_admin)
  const canCreateAccounts = ["admin", "client_admin", "org_admin"].includes(effectiveRole ?? "");

  const loadOrgMembers = async () => {
    if (!orgId) return;
    
    const { data: refreshedData } = await (supabase as any)
      .from("org_memberships")
      .select("user_id, member_role, created_at, email")
      .eq("org_id", orgId)
      .order("created_at", { ascending: false });

    if (refreshedData) {
      const mapped: Row[] = refreshedData.map((r: any) => ({
        user_id: r.user_id ?? null,
        email: r.email ?? null,
        member_role: r.member_role ?? null,
        created_at: r.created_at,
      }));
      setRows(mapped);
    }
  };

  useEffect(() => {
    (async () => {
      if (!orgId) return;
      setLoading(true);
      setErr(null);
      try {
        // 1) portfolio name (formerly org name)
        const { data: portfolio, error: portfolioErr } = await (supabase as any)
          .from("portfolios")
          .select("name")
          .eq("id", orgId)
          .maybeSingle();
        if (portfolioErr) {
          console.warn(portfolioErr);
          toast({
            title: "Access denied",
            description: "You may not have permission for this portfolio.",
          });
          return;
        }
        setOrgName(portfolio?.name ?? "");

        // 2) members from org_memberships
        const { data, error } = await (supabase as any)
          .from("org_memberships")
          .select("user_id, member_role, created_at, email")
          .eq("org_id", orgId)
          .order("created_at", { ascending: false });

        if (error) {
          console.warn(error);
          toast({
            title: "Access denied",
            description: "You may not have permission for this portfolio.",
          });
          return;
        }

        // Map the data
        const mapped: Row[] = (data ?? []).map((r: any) => ({
          user_id: r.user_id ?? null,
          email: r.email ?? null,
          member_role: r.member_role ?? null,
          created_at: r.created_at,
        }));
        setRows(mapped);
      } catch (e: any) {
        console.warn(e);
        setErr("Failed to load organization members.");
      } finally {
        setLoading(false);
      }
    })();
  }, [orgId, toast]);

  const handleAddUser = async (e: React.FormEvent) => {
    const sess = await supabase.auth.getSession();
    console.log("Current session in OrgUsers:", sess);
    
    e.preventDefault();
    if (!orgId) return;
    setInviteError(null);

    const trimmedEmail = newUserEmail.trim().toLowerCase();
    if (!trimmedEmail) {
      setInviteError("Email is required.");
      return;
    }

    setInviteLoading(true);
    try {
      const payload = {
        org_id: orgId,
        email: trimmedEmail,
        member_role: newUserRole,
        member_role_text: roleLabels[newUserRole] ?? newUserRole,
      };

      console.log("[OrgUsers] inserting org_membership with payload:", payload);

      const { data, error } = await supabase
        .from("org_memberships")
        .insert(payload)
        .select("*");

      if (error) {
        console.error("[OrgUsers] add user error", error);
        setInviteError(error.message ?? "Failed to add user to organization.");
        return;
      }

      console.log("[OrgUsers] membership created", data);

      // Clear the form on success
      setNewUserEmail("");
      setNewUserRole("respondent");
      setInviteError(null);

      // Refresh members list
      await loadOrgMembers();
    } catch (e: any) {
      console.error("[OrgUsers] add user error", e);
      setInviteError(e?.message ?? "Failed to add user to organization.");
    } finally {
      setInviteLoading(false);
    }
  };

  // Confirmation state
  const [confirmEmail, setConfirmEmail] = useState<string | null>(null);
  const [pendingCreateForm, setPendingCreateForm] = useState<{
    email: string; password: string; name: string; role: string;
  } | null>(null);

  const handleCreateAccountForOrg = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!orgId) return;
    setCreateAccountError(null);

    const email = newAccountEmail.trim().toLowerCase();
    if (!email || !newAccountPassword) {
      setCreateAccountError("Email and password are required.");
      return;
    }

    if (newAccountPassword.length < 6) {
      setCreateAccountError("Password must be at least 6 characters.");
      return;
    }

    // Show confirmation dialog
    setPendingCreateForm({
      email,
      password: newAccountPassword,
      name: newAccountDisplayName?.trim() || "",
      role: newAccountRole,
    });
    setConfirmEmail(email);
  };

  const handleConfirmedCreate = async () => {
    if (!orgId || !pendingCreateForm) return;
    const { email, password, name, role } = pendingCreateForm;
    setConfirmEmail(null);
    setPendingCreateForm(null);
    setCreateAccountLoading(true);

    try {
      const payload = {
        portfolio_id: orgId,
        email,
        password: password || undefined,
        display_name: name || null,
        member_role: role || "respondent",
      };
      console.log("[create-user payload]", payload);

      if (!payload.member_role) {
        setCreateAccountError("Role is required");
        setCreateAccountLoading(false);
        return;
      }

      const { data, error } = await supabase.functions.invoke("create-user", {
        body: payload,
      });

      if (error) {
        console.error("[OrgUsers] Edge function error", error);
        setCreateAccountError(error.message ?? "Failed to create user.");
        return;
      }
      if (data?.error) {
        console.error("[OrgUsers] Edge function returned error", data.error);
        setCreateAccountError(data.error);
        return;
      }

      console.log("[OrgUsers] user created successfully:", data);

      // Verify returned email matches what we sent
      if (data?.email && data.email.toLowerCase() !== email) {
        console.error("[OrgUsers] EMAIL MISMATCH! Sent:", email, "Got:", data.email);
        setCreateAccountError(`Email mismatch: sent ${email} but server returned ${data.email}. Contact support.`);
        return;
      }

      setNewAccountEmail("");
      setNewAccountPassword("");
      setNewAccountDisplayName("");
      setNewAccountRole("respondent");
      setCreateAccountError(null);

      toast({
        title: "User created",
        description: `Account for ${email} created and assigned to this organization.`,
      });

      await loadOrgMembers();
    } catch (err: any) {
      console.error("[OrgUsers] create account error", err);
      setCreateAccountError(err?.message ?? "Failed to create user.");
    } finally {
      setCreateAccountLoading(false);
    }
  };

  return (
    <AppLayout title="Org Users">
      <div className="p-6 space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-semibold">
              Users — {orgName || "Organization"}
            </h1>
            <p className="text-sm text-muted-foreground">
              Manage users for this organization.
            </p>
          </div>
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => {
              if (effectiveRole === "portfolio_manager") {
                navigate("/pm");
              } else if (effectiveRole === "client_admin") {
                navigate("/client/sub-orgs");
              } else if (effectiveRole === "admin") {
                navigate("/admin");
              } else {
                navigate("/org-admin");
              }
            }}
          >
            {effectiveRole === "portfolio_manager" 
              ? "Back to Portfolio Dashboard" 
              : "Back to Org Admin"}
          </Button>
        </div>

        {err && (
          <Alert variant="destructive">
            <AlertDescription>{err}</AlertDescription>
          </Alert>
        )}

        {/* Create login account form - only for privileged users */}
        {canCreateAccounts && orgId && (
          <Card className="p-4 space-y-4 border-primary/20">
            <h2 className="font-semibold text-lg">Create login account for this org</h2>
            <p className="text-sm text-muted-foreground">
              Create a new user account with email and password, automatically assigned to this organization.
            </p>

            <form onSubmit={handleCreateAccountForOrg} className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-1">
                  <Label htmlFor="account-email">Email</Label>
                  <Input
                    id="account-email"
                    type="email"
                    value={newAccountEmail}
                    onChange={(e) => setNewAccountEmail(e.target.value)}
                    placeholder="user@example.com"
                    required
                  />
                </div>

                <div className="space-y-1">
                  <Label htmlFor="account-password">Temporary password</Label>
                  <Input
                    id="account-password"
                    type="password"
                    value={newAccountPassword}
                    onChange={(e) => setNewAccountPassword(e.target.value)}
                    placeholder="Min 6 characters"
                    required
                  />
                </div>
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-1">
                  <Label htmlFor="account-name">Display name (optional)</Label>
                  <Input
                    id="account-name"
                    type="text"
                    value={newAccountDisplayName}
                    onChange={(e) => setNewAccountDisplayName(e.target.value)}
                    placeholder="Full name"
                  />
                </div>

                <div className="space-y-1">
                  <Label htmlFor="account-role">Role in this org</Label>
                  <select
                    id="account-role"
                    className="border rounded-md px-2 py-2 text-sm bg-background w-full h-10"
                    value={newAccountRole}
                    onChange={(e) => setNewAccountRole(e.target.value as "respondent" | "reviewer" | "org_admin")}
                  >
                    <option value="respondent">Respondent</option>
                    <option value="reviewer">Reviewer</option>
                    <option value="org_admin">Portfolio Manager</option>
                  </select>
                </div>
              </div>

              <div className="flex items-center gap-4">
                <Button type="submit" disabled={createAccountLoading}>
                  {createAccountLoading ? "Creating…" : "Create user & assign to org"}
                </Button>
              </div>

              {createAccountError && (
                <p className="text-xs text-destructive">{createAccountError}</p>
              )}
            </form>
          </Card>
        )}

        {/* Add user form (email-only membership) */}
        <Card className="p-4 space-y-4">
          <h2 className="font-semibold text-lg">Add existing user to this organization</h2>
          <p className="text-sm text-muted-foreground">
            Enter the email of an existing user and choose their role in this
            organization.
          </p>

          <form
            onSubmit={handleAddUser}
            className="grid gap-4 md:grid-cols-[minmax(0,2fr)_minmax(0,1.2fr)_auto]"
          >
            <div className="space-y-1">
              <Label htmlFor="user-email">User email</Label>
              <Input
                id="user-email"
                type="email"
                value={newUserEmail}
                onChange={(e) => setNewUserEmail(e.target.value)}
                placeholder="person@company.com"
                required
              />
            </div>

            <div className="space-y-1">
              <Label htmlFor="user-role">Role in this org</Label>
              <select
                id="user-role"
                className="border rounded-md px-2 py-2 text-sm bg-background w-full h-10"
                value={newUserRole}
                onChange={(e) => setNewUserRole(e.target.value as "respondent" | "reviewer" | "org_admin")}
              >
                <option value="respondent">Respondent</option>
                <option value="reviewer">Reviewer</option>
                <option value="org_admin">Portfolio Manager</option>
              </select>
            </div>

            <div className="flex items-end">
              <Button type="submit" disabled={inviteLoading}>
                {inviteLoading ? "Adding…" : "Add User"}
              </Button>
            </div>
          </form>

          {inviteError && (
            <p className="text-xs text-destructive mt-1">{inviteError}</p>
          )}
        </Card>

        {/* Members list */}
        <Card className="p-4">
          {loading ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : rows.length === 0 ? (
            <p className="text-sm text-muted-foreground">No members found.</p>
          ) : (
            <div className="divide-y">
              {rows.map((r, idx) => (
                <div
                  key={r.user_id ?? r.email ?? idx}
                  className="py-3 flex items-center justify-between"
                >
                  <div>
                    <div className="font-medium">
                      {r.email ?? "Unknown email"}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {r.user_id 
                        ? `ID: ${r.user_id.slice(0, 8)}...` 
                        : "Pending user account"}
                    </div>
                  </div>
                  <div className="text-sm text-muted-foreground">
                    {roleLabels[r.member_role ?? ""] ?? r.member_role ?? "Member"}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* Confirmation dialog for user creation */}
        <AlertDialog open={!!confirmEmail} onOpenChange={(open) => {
          if (!open) {
            setConfirmEmail(null);
            setPendingCreateForm(null);
          }
        }}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Confirm User Creation</AlertDialogTitle>
              <AlertDialogDescription>
                Create user with email: <strong className="text-foreground">{confirmEmail}</strong>?
                <br />
                Role: <strong className="text-foreground">{roleLabels[pendingCreateForm?.role ?? ""] ?? pendingCreateForm?.role}</strong>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handleConfirmedCreate}>
                Create User
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </AppLayout>
  );
}
